import { gql } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { buildAgencies, buildContacts, buildLocations } from '.';
import { Option } from '../types';

const AUTO_COMPLETE_QUERY = gql`
  query FindAndAgentAutocomplete(
    $input: String!
    $maxResults: Int
    $locationTypes: [LocationSuggestionType]
    $imageFormat: ImageFormat
    $fitIn: Boolean
    $trim: ImageTrim
  ) {
    suggestLocations(
      prefixText: $input
      locationTypes: $locationTypes
      maxResults: $maxResults
    ) {
      ... on SuburbSuggestion {
        displayName
        nameSlug
        name
        state
        postcode
      }
    }
    suggestedAgencies(name: $input, pageSize: $maxResults) {
      agencyName
      profileUrl
      backgroundColour
      suburb
      state
      postcode
      logo {
        url(imageFormat: $imageFormat, fitIn: $fitIn, trim: $trim)
      }
    }
    suggestedContacts(name: $input, pageSize: $maxResults) {
      id
      contactId
      name
      thumbnail
      profileUrl
      agencyId
      agencyName
      suburb
      state
    }
  }
`;

// Max results shown per category section (LOCATIONS / AGENTS / AGENCIES).
// The typeahead's built-in per-group caps aren't configurable and default
// higher, so we cap each group here to match the design.
const V5_MAX_RESULTS_PER_GROUP = 4;
const V3_MAX_RESULTS_PER_GROUP = 10;

const suggestLocations = async ({
  input,
  apolloClient,
  setError,
  setNoResults,
  useTypeaheadV5 = false,
}: {
  input: string;
  apolloClient: ApolloClient<any>;
  setError: (error: string | null) => void;
  setNoResults: (error: string | null) => void;
  useTypeaheadV5?: boolean;
}): Promise<Option[]> => {
  const maxResultsPerGroup = useTypeaheadV5
    ? V5_MAX_RESULTS_PER_GROUP
    : V3_MAX_RESULTS_PER_GROUP;

  const response = await apolloClient.query({
    query: AUTO_COMPLETE_QUERY,
    variables: {
      input,
      maxResults: maxResultsPerGroup,
      locationTypes: ['SUBURB'],
      imageFormat: 'WEBP',
      fitIn: true,
      trim: 'DEFAULT',
    },
    errorPolicy: 'all', // https://www.apollographql.com/docs/react/v2/data/error-handling/#error-policies
  });

  const allGroupsAreEmpty =
    response.data?.suggestLocations?.length === 0 &&
    response.data?.suggestedAgencies?.length === 0 &&
    response.data?.suggestedContacts?.length === 0;

  if (response.error) {
    setError('There was an error connecting with the server.');
    setNoResults(null);

    return [];
  }

  if (allGroupsAreEmpty) {
    setError(null);
    setNoResults('No location found, please try another search.');

    return [];
  }

  setError(null);
  setNoResults(null);

  const results = [
    ...buildLocations(response.data.suggestLocations).slice(
      0,
      maxResultsPerGroup,
    ),
    ...buildAgencies(response.data.suggestedAgencies).slice(
      0,
      maxResultsPerGroup,
    ),
    ...buildContacts(response.data.suggestedContacts).slice(
      0,
      maxResultsPerGroup,
    ),
  ];

  // De-duplicate results
  return results.reduce<Option[]>((acc, current) => {
    if (!acc.some(({ value }) => value === current.value)) {
      acc.push(current);
    }

    return acc;
  }, []);
};

export default suggestLocations;
